import {
  InformationCircleIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';
import { Input, InputGroup, Button, Alert } from 'react-daisyui';
import { useRollbar } from '@rollbar/react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment, { DurationInputArg2 } from 'moment';
import { Logo } from '@/components/Logo';
import { Spinner } from '@/components/Spinner';
import PageContainer from '@/components/PageContainer';
import { BrandShadowBox } from '@/components/BrandShadowBox';
import { loginWithPassword } from '@/utils/auth';
import { AuthCookie, RefreshAuthResponse } from '@/types';
import { IUserContext, useUserContext } from '@/context/UserContext';

async function completeMcpAuth(
  pendingCode: string,
  token: string,
  retry = true
): Promise<void> {
  const response = await fetch('/mcp/complete-auth', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ pendingCode }),
  });

  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    redirectUrl?: string;
  } & Partial<RefreshAuthResponse>;

  if (response.status === 403 && retry) {
    const { refreshAuthentication } = body;
    if (refreshAuthentication) {
      const { user, access_token: accessToken, maxAge } = refreshAuthentication;
      const date = moment()
        .add(maxAge[0], maxAge[1] as DurationInputArg2)
        .toDate();
      if (accessToken) localStorage.setItem('access_token', accessToken);
      localStorage.setItem('User', JSON.stringify(user));
      if (maxAge) localStorage.setItem('expiresIn', date.getTime().toString());
      localStorage.setItem('authentication_refreshed', 'true');
      await completeMcpAuth(pendingCode, accessToken, false);
      return;
    }
  }

  if (!response.ok) {
    throw new Error(body.error ?? 'Failed to complete MCP authentication');
  }

  window.location.href = body.redirectUrl ?? '';
}

export default function McpAuth() {
  const [searchParams] = useSearchParams();
  const pendingCode = searchParams.get('pending_code') ?? '';
  const rollbar = useRollbar();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { auth, setAuth }: IUserContext = useUserContext();
  const completedRef = useRef(false);

  // If the user is already logged in, complete auth automatically
  useEffect(() => {
    if (!auth?.token || !pendingCode || completedRef.current) return;
    completedRef.current = true;
    setIsLoading(true);
    completeMcpAuth(pendingCode, auth.token).catch((err: Error) => {
      completedRef.current = false;
      rollbar.error('MCP auto-auth failed', { error: err });
      setErrorMessage(
        err?.message || 'Could not connect to Claude. Please try again.'
      );
      setIsLoading(false);
    });
  }, [auth?.token, pendingCode, rollbar]);

  if (!pendingCode) {
    return (
      <PageContainer name='mcp-auth'>
        <div className='h-full flex flex-col items-center pt-40 mx-auto'>
          <BrandShadowBox>
            <p className='text-sm text-center p-4'>
              Invalid MCP authorization request.
            </p>
          </BrandShadowBox>
        </div>
      </PageContainer>
    );
  }

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    try {
      const resp = (await loginWithPassword(email, password)) as AuthCookie;
      if (!resp?.access_token) throw new Error('Invalid email or password.');
      if (setAuth) {
        const { maxAge } = resp;
        const expires = maxAge
          ? moment()
              .add(maxAge[0], maxAge[1] as DurationInputArg2)
              .toDate()
          : undefined;
        setAuth(resp.user, resp.access_token, expires);
        // completeMcpAuth is called by the useEffect once auth.token updates
      }
    } catch (err) {
      const message =
        (err as Error).message || 'An error occurred. Please try again.';
      rollbar.error('MCP login failed', { error: err });
      setErrorMessage(message);
      setIsLoading(false);
    }
  };

  return (
    <PageContainer name='mcp-auth'>
      <div className='h-full flex flex-col items-center pt-40 mx-auto'>
        <BrandShadowBox className='flex flex-col items-center'>
          <Logo
            className='text-2xl ml-4 items-center w-[160px]'
            iconClassName='h-[41px] w-auto object-contain'
          />
          <p className='text-sm mt-4 mb-2 text-center'>
            Sign in to connect Opstart with Claude
          </p>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={submitHandler}
            className='w-[80%] flex flex-col justify-center'
          >
            <div className='w-full mt-4'>
              <InputGroup>
                <Input
                  type='email'
                  placeholder='login email'
                  className='w-full'
                  color='accent'
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </InputGroup>
            </div>
            <div className='w-full mt-4'>
              <InputGroup>
                <Input
                  type='password'
                  placeholder='password'
                  className='w-full'
                  color='accent'
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </InputGroup>
            </div>
            <div className='mt-4 text-center'>
              <Button
                type='submit'
                color='accent'
                className='btn btn-accent'
                disabled={isLoading}
              >
                Connect
                {isLoading ? (
                  <Spinner className='w-6 h-6 ml-2' />
                ) : (
                  <ArrowRightCircleIcon className='w-6 h-6 ml-2' />
                )}
              </Button>
            </div>
            <br />
          </form>
          {errorMessage && (
            <Alert
              className='rounded-2xl opacity-100 my-4 text-sm text-center w-[80%]'
              icon={<InformationCircleIcon className='w-8 h-8' />}
              status='error'
            >
              {errorMessage}
            </Alert>
          )}
        </BrandShadowBox>
      </div>
    </PageContainer>
  );
}
